/**
 * Whitelist for `background-color` values injected from admin settings.
 * Rejects any character that could break out of the CSS rule (`}`, `;`, `<`, `\`, comment markers, etc.)
 * and caps length as defense-in-depth. Browser-level color parsing handles the rest.
 */
function isSafeCssColor(value) {
  if (typeof value !== 'string') return false;
  const v = value.trim();
  if (!v || v.length > 64) return false;
  if (/[{};<>\\"'`\n\r]/.test(v)) return false;
  if (v.includes('/*') || v.includes('*/')) return false;
  return true;
}

/** Height animation for PDP accordions — ports live `app/utilities/accordion.js`. */
window.JR = window.JR || {};
if (typeof window.JR.toggleProductAccordion !== 'function') {
  const PH_ACCORDION_ANIMATION_MS = 425;
  const PH_ACCORDION_RENDER_DELAY_MS = 25;

  function phAccordionPanel(btn) {
    const panel = btn._phAccordionPanel || btn.nextElementSibling;
    if (
      panel &&
      (panel.classList.contains('ph-accordion__body') || panel.classList.contains('phn-accordion__body'))
    ) {
      btn._phAccordionPanel = panel;
      return panel;
    }
    return null;
  }

  function phAccordionSyncIcon(btn, expanded) {
    const icon = btn.querySelector('.phn-accordion__icon, .ph-accordion__icon');
    if (icon) icon.textContent = expanded ? '−' : '+';
  }

  function phInitProductAccordion(btn) {
    const panel = phAccordionPanel(btn);
    if (!panel) return;
    const isExpanded = btn.getAttribute('aria-expanded') === 'true';
    panel.removeAttribute('hidden');
    panel.setAttribute('aria-hidden', isExpanded ? 'false' : 'true');
    panel.style.height = isExpanded ? 'auto' : '0px';
    phAccordionSyncIcon(btn, isExpanded);
  }

  function phSetProductAccordionExpanded(btn, expanded) {
    const panel = phAccordionPanel(btn);
    if (!panel) return;
    clearTimeout(btn._phAccordionRenderTimeout);
    clearTimeout(btn._phAccordionAnimationTimeout);
    btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    panel.removeAttribute('hidden');
    panel.setAttribute('aria-hidden', expanded ? 'false' : 'true');
    panel.style.height = expanded ? 'auto' : '0px';
    phAccordionSyncIcon(btn, expanded);
  }

  function phToggleProductAccordion(btn) {
    const panel = phAccordionPanel(btn);
    if (!panel) return;
    const contentEl = panel.firstElementChild;
    const willExpand = btn.getAttribute('aria-expanded') !== 'true';
    const height = contentEl ? contentEl.scrollHeight : panel.scrollHeight;

    clearTimeout(btn._phAccordionRenderTimeout);
    clearTimeout(btn._phAccordionAnimationTimeout);

    btn.setAttribute('aria-expanded', willExpand ? 'true' : 'false');
    panel.setAttribute('style', willExpand ? 'height: 0px' : `height: ${height}px`);

    btn._phAccordionRenderTimeout = setTimeout(() => {
      panel.setAttribute('style', willExpand ? `height: ${height}px` : 'height: 0px');
      panel.setAttribute('aria-hidden', willExpand ? 'false' : 'true');
    }, PH_ACCORDION_RENDER_DELAY_MS);

    btn._phAccordionAnimationTimeout = setTimeout(() => {
      panel.setAttribute('style', willExpand ? 'height: auto' : 'height: 0px');
    }, PH_ACCORDION_ANIMATION_MS);

    phAccordionSyncIcon(btn, willExpand);
  }

  function phInitProductAccordions(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope
      .querySelectorAll('[data-ph-accordion-toggle], [data-phn-accordion-toggle], .js-quiz-alt-accordion')
      .forEach((btn) => {
        phInitProductAccordion(btn);
      });
  }

  window.JR.initProductAccordion = phInitProductAccordion;
  window.JR.initProductAccordions = phInitProductAccordions;
  window.JR.toggleProductAccordion = phToggleProductAccordion;
  window.JR.setProductAccordionExpanded = phSetProductAccordionExpanded;
}

/** Ingredients modal — ports live `productIngredients.js` (.open class toggle). */
if (typeof window.JR.initIngredientsModal !== 'function') {
  function phIngredientsModalClick(root, e) {
    const toggle = e.target.closest('.js-ingredientsToggle');
    if (!toggle || !root.contains(toggle)) return;
    const modal = root.querySelector('.js-ingredientsModal');
    if (!modal) return;
    modal.classList.toggle('open');
    modal.setAttribute('aria-hidden', modal.classList.contains('open') ? 'false' : 'true');
  }

  function phInitIngredientsModal(root) {
    if (!root || root._phIngredientsClickHandler) return;
    root._phIngredientsClickHandler = (e) => phIngredientsModalClick(root, e);
    root.addEventListener('click', root._phIngredientsClickHandler);
  }

  function phDestroyIngredientsModal(root) {
    if (!root || !root._phIngredientsClickHandler) return;
    root.removeEventListener('click', root._phIngredientsClickHandler);
    root._phIngredientsClickHandler = null;
  }

  window.JR.initIngredientsModal = phInitIngredientsModal;
  window.JR.destroyIngredientsModal = phDestroyIngredientsModal;
}

function injectProductSwatchColors(sectionId) {
  const el = document.getElementById(`product-swatch-colors-${sectionId}`);
  if (!el) return;
  let raw;
  try {
    raw = JSON.parse(el.textContent);
  } catch {
    return;
  }
  if (!raw || typeof raw !== 'string') return;
  let css = '';
  raw.split('\n').forEach((line) => {
    line = line.trim();
    if (!line) return;
    let idx = line.indexOf('==');
    let name;
    let color;
    if (idx > 0) {
      name = line.substring(0, idx).trim();
      color = line.substring(idx + 2).trim();
    } else {
      idx = line.indexOf('=');
      if (idx > 0) {
        name = line.substring(0, idx).trim();
        color = line.substring(idx + 1).trim();
      }
    }
    if (!name || !color || !isSafeCssColor(color)) return;
    const handle = name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
    css += `.ph-swatch[data-shade="${handle}"] .ph-swatch__color{background-color:${color} !important;background-image:none !important;}`;
    css += `[data-shade="${handle}"].ph-sticky-bar__shade-swatch{background-color:${color} !important;background-image:none !important;}`;
    css += `[data-shade="${handle}"].ph-sticky-bar__dropdown-swatch{background-color:${color} !important;background-image:none !important;}`;
    css += `.ph-paired-card .ph-swatch[data-shade="${handle}"] .ph-swatch__color{background-color:${color} !important;background-image:none !important;}`;
  });
  if (css) {
    const s = document.createElement('style');
    s.setAttribute('data-ph-swatch-style', sectionId);
    s.textContent = css;
    document.head.appendChild(s);
  }
}

function getKlaviyoListId(sectionId) {
  const el = document.getElementById(`product-klaviyo-list-${sectionId}`);
  if (!el) return '';
  try {
    const v = JSON.parse(el.textContent);
    return typeof v === 'string' ? v : '';
  } catch {
    return '';
  }
}

/**
 * Refresh any `[data-ajax-cart-section]` regions in-place after a cart mutation.
 * Hoisted to `window.JR.refreshAjaxCartSections` so other scripts (e.g.
 * `section-related-products-v2.js`) can reuse the same implementation instead
 * of duplicating it.
 */
window.JR = window.JR || {};
if (typeof window.JR.refreshAjaxCartSections !== 'function') {
  window.JR.refreshAjaxCartSections = function refreshAjaxCartSections() {
    document.querySelectorAll('[data-ajax-cart-section]').forEach((cartEl) => {
      const section = cartEl.closest('[id^="shopify-section-"]');
      if (!section) return;
      const sid = section.id.replace('shopify-section-', '');
      fetch(`${window.location.pathname}?sections=${sid}`)
        .then((r) => r.json())
        .then((data) => {
          if (!data[sid]) return;
          const doc = new DOMParser().parseFromString(data[sid], 'text/html');
          const fresh = doc.querySelector('[data-ajax-cart-section]');
          if (fresh) cartEl.innerHTML = fresh.innerHTML;
        })
        .catch(() => {});
    });
  };
}
const refreshAjaxCartSections = window.JR.refreshAjaxCartSections;

if (typeof window.JR.refreshCartAndOpen !== 'function') {
  window.JR.refreshCartAndOpen = function refreshCartAndOpen() {
    if (window.liquidAjaxCart && typeof window.liquidAjaxCart.update === 'function') {
      window.liquidAjaxCart.update({}, {});
    }
    if (typeof window.JR.refreshAjaxCartSections === 'function') {
      window.JR.refreshAjaxCartSections();
    }
    window.dispatchEvent(new CustomEvent('cart-open'));
  };
}

function cartAddVariant(variantId, quantity = 1) {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  return fetch(`${root}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: [{ id: variantId, quantity }] }),
  }).then((r) => r.json());
}

if (typeof window.__phPairAdd !== 'function') {
  window.__phPairAdd = function (variantId, done) {
    cartAddVariant(variantId, 1)
      .then(() => {
        window.JR.refreshCartAndOpen();
      })
      .finally(() => {
        if (done) done();
      });
  };
}

/** Canonical pathname for `data-url` (e.g. `/products/handle`) */
function productJsonCacheKey(datasetUrl) {
  if (!datasetUrl || typeof datasetUrl !== 'string') return '';
  datasetUrl = datasetUrl.trim();
  try {
    const base = window.location?.origin || 'https://shop.example';
    const u = datasetUrl.startsWith('http') ? new URL(datasetUrl) : new URL(datasetUrl, base);
    return u.pathname || '';
  } catch {
    return datasetUrl.split('?')[0] || '';
  }
}

/** Parsed `script[data-product]` by product path; shared if multiple `product-info` use the same product */
const productJsonCacheByUrl = new Map();

const SECTION_HTML_CACHE_MAX = 24;
const sectionHtmlCache = new Map();
const sectionHtmlCacheOrder = [];
const sectionHtmlInflight = new Map();

function sectionHtmlCacheKey(productUrl, sectionId, optionValueIds, variantIdFallback) {
  const base = productJsonCacheKey(productUrl) || String(productUrl).split('?')[0];
  const ids = Array.isArray(optionValueIds) ? optionValueIds.join(',') : String(optionValueIds);
  const vf =
    variantIdFallback != null && variantIdFallback !== '' ? `|vf:${variantIdFallback}` : '';
  return `${base}|${sectionId}|${ids}${vf}`;
}

function rememberSectionHtmlCache(key, text) {
  if (sectionHtmlCache.has(key)) {
    sectionHtmlCache.set(key, text);
    return;
  }
  sectionHtmlCache.set(key, text);
  sectionHtmlCacheOrder.push(key);
  while (sectionHtmlCacheOrder.length > SECTION_HTML_CACHE_MAX) {
    const oldest = sectionHtmlCacheOrder.shift();
    sectionHtmlCache.delete(oldest);
  }
}

/**
 * Deduped section fetch: cache hit, shared in-flight promise, or new network request.
 * @returns {Promise<string>}
 */
function loadSectionHtmlText(productUrl, sectionId, optionValueIds, variantIdFallback = null) {
  const key = sectionHtmlCacheKey(productUrl, sectionId, optionValueIds, variantIdFallback);
  if (sectionHtmlCache.has(key)) {
    return Promise.resolve(sectionHtmlCache.get(key));
  }
  if (sectionHtmlInflight.has(key)) {
    return sectionHtmlInflight.get(key);
  }
  const joined = Array.isArray(optionValueIds)
    ? optionValueIds.filter((x) => x != null && x !== '').join(',')
    : '';
  const qsParts = [`section_id=${encodeURIComponent(sectionId)}`];
  if (joined) {
    qsParts.push(`option_values=${encodeURIComponent(joined)}`);
  } else if (variantIdFallback != null && variantIdFallback !== '') {
    qsParts.push(`variant=${encodeURIComponent(String(variantIdFallback))}`);
  }
  const sep = productUrl.includes('?') ? '&' : '?';
  const url = `${productUrl}${sep}${qsParts.join('&')}`;
  const p = fetch(url)
    .then((r) => r.text())
    .then((text) => {
      sectionHtmlInflight.delete(key);
      rememberSectionHtmlCache(key, text);
      return text;
    })
    .catch((err) => {
      sectionHtmlInflight.delete(key);
      throw err;
    });
  sectionHtmlInflight.set(key, p);
  return p;
}

export class ProductInfo extends HTMLElement {
  swiper = undefined;

  /** Guards against applying stale section HTML when the shopper changes options quickly */
  _sectionRenderGen = 0;

  /** @type {object | false | undefined} Parsed `script[data-product]`; `false` = missing/invalid */
  _productJsonCache = undefined;

  constructor() {
    super();
    this._boundHandlers = {
      onVariantChange: this.onVariantChange.bind(this),
      onVariantOptionHover: this.onVariantOptionHover.bind(this),
      onQuantitySelectorEvent: this.onQuantitySelectorEvent.bind(this),
      onDelegatedClick: this.onDelegatedClick.bind(this),
      onDocumentClickClose: this.onDocumentClickClose.bind(this),
      onCartRequestStart: this.onCartRequestStart.bind(this),
      onCartRequestEnd: this.onCartRequestEnd.bind(this),
      onWindowScrollStickyBar: null,
    };
  }

  setupEventListeners() {
    const h = this._boundHandlers;
    this.variantSelector?.addEventListener('change', h.onVariantChange);
    this.addEventListener('mouseover', h.onVariantOptionHover);
    const qs = this.quantitySelector;
    if (qs) {
      qs.addEventListener('change', h.onQuantitySelectorEvent);
      qs.querySelector('button[name="plus"]')?.addEventListener('click', h.onQuantitySelectorEvent);
      qs.querySelector('button[name="minus"]')?.addEventListener('click', h.onQuantitySelectorEvent);
    }
    this.addEventListener('click', h.onDelegatedClick);
    document.addEventListener('click', h.onDocumentClickClose);
  }

  connectedCallback() {
    injectProductSwatchColors(this.dataset.section);
    this.setupEventListeners();
    window.JR.initProductAccordions(this);
    window.JR.initIngredientsModal(this);
    this.initSwiper();
    this.initStickyBar();
    this.initNotifyModal();
    this.initFindationModal();
    this.initPairWithCards();
    this.listenCartEvents();
    this.warmProductJsonCache();
    /* Re-fetch the main product section once after paint so the gallery matches the
       Section Rendering API output for the selected variant (variant-focused media).
       Full-page Liquid alone can disagree until the first variant interaction. */
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.hydrateInitialGalleryOnLoad();
      });
    });
  }

  /**
   * Tear down listeners attached to `document` / `window` so Theme Editor section
   * reloads (and any other re-mount) do not leak stale handlers / instances.
   */
  disconnectedCallback() {
    const h = this._boundHandlers || {};
    document.removeEventListener('click', h.onDocumentClickClose);
    document.removeEventListener('liquid-ajax-cart:request-start', h.onCartRequestStart);
    document.removeEventListener('liquid-ajax-cart:request-end', h.onCartRequestEnd);
    if (h.onWindowScrollStickyBar) {
      window.removeEventListener('scroll', h.onWindowScrollStickyBar);
      h.onWindowScrollStickyBar = null;
    }
    this.destroySwiper();
    window.JR.destroyIngredientsModal(this);
    this._didHydrateInitialGallery = false;
    const styleEl = document.querySelector(`style[data-ph-swatch-style="${this.dataset.section}"]`);
    if (styleEl) styleEl.remove();
  }

  /** Parse embedded product JSON once at mount (and reuse from module cache when possible). */
  warmProductJsonCache() {
    this.getProductJson();
  }

  /**
   * First paint uses full-page Liquid; variant-focused galleries often only fully
   * resolve once the Section Rendering API runs with the active variant (same path
   * as changing a swatch). Mirror that fetch once on load so default-variant media
   * appears without forcing the shopper to interact first.
   */
  hydrateInitialGalleryOnLoad() {
    if (this._didHydrateInitialGallery) return;
    if (this.dataset.updateUrl === 'false') return;

    const sectionId = this.dataset.section;
    let optionValues = this.selectedOptionValues;
    let variantFallback = null;

    if (optionValues.length === 0) {
      const scr = this.variantSelector?.querySelector('script[data-selected-variant]');
      if (scr) {
        try {
          const v = JSON.parse(scr.textContent);
          if (v?.id) variantFallback = v.id;
        } catch {
          /* ignore */
        }
      }
    }

    if (optionValues.length === 0 && !variantFallback) return;

    this._didHydrateInitialGallery = true;

    loadSectionHtmlText(this.dataset.url, sectionId, optionValues, variantFallback)
      .then((responseText) => {
        this.applySectionHtmlResponse(responseText, false);
      })
      .catch(() => {});
  }

  onDelegatedClick(e) {
    const t = e.target;
    if (t.closest('[data-ph-swatch-tabs]')) {
      const btn = t.closest('.ph-swatch-tab');
      if (!btn) return;
      e.preventDefault();
      const tabsEl = t.closest('[data-ph-swatch-tabs]');
      const tab = btn.dataset.tab;
      tabsEl.querySelectorAll('.ph-swatch-tab').forEach((b) => b.classList.remove('ph-swatch-tab--active'));
      btn.classList.add('ph-swatch-tab--active');
      const swatchesEl = tabsEl.nextElementSibling;
      if (!swatchesEl) return;
      swatchesEl.querySelectorAll('.ph-swatch').forEach((label) => {
        const cat = label.dataset.tabCategory || '';
        const show = tab === 'all' || cat === tab;
        label.classList.toggle('ph-swatch--hidden', !show);
        const input = swatchesEl.querySelector(`#${label.getAttribute('for')}`);
        if (input) input.classList.toggle('ph-swatch--hidden', !show);
      });
      return;
    }
    if (t.closest('[data-ph-sticky-atc]')) {
      e.preventDefault();
      this.stickyAddToCart();
    }
    if (t.closest('[data-ph-notify-btn]')) {
      e.preventDefault();
      this.openNotifyModal();
    }
    if (t.closest('[data-ph-sticky-shade-toggle]')) {
      e.preventDefault();
      const wrap = t.closest('[data-ph-sticky-shade]');
      const dd = wrap?.querySelector('[data-ph-sticky-dropdown]');
      const btn = wrap?.querySelector('[data-ph-sticky-shade-toggle]');
      if (!dd || !btn) return;
      const open = dd.hasAttribute('hidden');
      if (open) {
        dd.removeAttribute('hidden');
        btn.setAttribute('aria-expanded', 'true');
      } else {
        dd.setAttribute('hidden', '');
        btn.setAttribute('aria-expanded', 'false');
      }
    }
    if (t.closest('.ph-sticky-bar__dropdown-item')) {
      const item = t.closest('.ph-sticky-bar__dropdown-item');
      const vid = item?.dataset?.optionValueId;
      if (!vid || !this.variantSelector) return;
      e.preventDefault();
      const input = this.variantSelector.querySelector(`input[data-option-value-id="${vid}"]`);
      if (input && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      const wrap = item.closest('[data-ph-sticky-shade]');
      wrap?.querySelector('[data-ph-sticky-dropdown]')?.setAttribute('hidden', '');
      wrap?.querySelector('[data-ph-sticky-shade-toggle]')?.setAttribute('aria-expanded', 'false');
    }
    const pairFind = t.closest('[data-ph-pair-find-shade]');
    if (pairFind) {
      e.preventDefault();
      const card = pairFind.closest('[data-ph-pair-card]');
      pairFind.classList.add('ph-hidden');
      card?.querySelector('[data-ph-pair-swatches]')?.classList.remove('ph-hidden');
      card?.querySelector('[data-ph-pair-atc-wrap]')?.classList.remove('ph-hidden');
    }
    const sw = t.closest('[data-ph-pair-swatch]');
    if (sw) {
      e.preventDefault();
      const card = sw.closest('[data-ph-pair-card]');
      const vid = sw.dataset.phPairSwatch;
      const price = sw.dataset.phPairPrice;
      const label = sw.getAttribute('title') || '';
      card?.querySelectorAll('[data-ph-pair-swatch]').forEach((b) => b.classList.remove('ph-swatch--active'));
      sw.classList.add('ph-swatch--active');
      const addBtn = card?.querySelector('[data-ph-pair-add]');
      if (addBtn && vid) addBtn.setAttribute('data-ph-pair-add', vid);
      const priceEl = card?.querySelector('[data-ph-pair-atc-wrap] .ph-paired-btn__price');
      if (priceEl && price) {
        const cents = parseInt(price, 10);
        priceEl.textContent = cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`;
      }
      const sl = card?.querySelector('[data-ph-pair-shade-label]');
      if (sl) {
        sl.textContent = label;
        sl.classList.remove('ph-hidden');
      }
    }
    const pairAdd = t.closest('[data-ph-pair-add]');
    if (pairAdd && !pairAdd.closest('[data-ph-pair-find-shade]')) {
      e.preventDefault();
      const vid = parseInt(pairAdd.getAttribute('data-ph-pair-add'), 10);
      if (!vid) return;
      pairAdd.disabled = true;
      window.__phPairAdd(vid, () => {
        pairAdd.disabled = false;
      });
    }
    const accToggle = t.closest('[data-ph-accordion-toggle]');
    if (accToggle) {
      e.preventDefault();
      window.JR.toggleProductAccordion(accToggle);
    }
  }

  onDocumentClickClose(e) {
    if (!e.target.closest('.ph-sticky-bar__shade')) {
      this.querySelectorAll('[data-ph-sticky-dropdown]').forEach((dd) => dd.setAttribute('hidden', ''));
      this.querySelectorAll('[data-ph-sticky-shade-toggle]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
    }
  }

  initStickyBar() {
    const section = this.querySelector('[data-ph-product-section]');
    if (!section) return;
    const onScroll = () => {
      const bar = this.querySelector('[data-ph-sticky-bar]');
      if (!bar) return;
      const rect = section.getBoundingClientRect();
      const h = section.offsetHeight;
      const scrolledPast = -rect.top;
      const visible = scrolledPast > h * 0.3 && rect.bottom > 0;
      if (visible) {
        bar.classList.add('ph-sticky-bar--visible');
      } else {
        bar.classList.remove('ph-sticky-bar--visible');
      }
    };
    this._boundHandlers.onWindowScrollStickyBar = onScroll;
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  stickyAddToCart() {
    const idInput = this.querySelector(`#product-form-${this.dataset.section} input[name="id"]`);
    const vid = idInput?.value;
    if (!vid) return;
    const stickyBtn = this.querySelector('[data-ph-sticky-atc]');
    const mainBtn = this.querySelector('[data-ph-main-atc]');
    this.setAtcLoading(stickyBtn, true);
    this.setAtcLoading(mainBtn, true);
    cartAddVariant(vid, 1)
      .then(() => {
        window.dispatchEvent(new CustomEvent('cart-open'));
        refreshAjaxCartSections();
      })
      .finally(() => {
        this.setAtcLoading(stickyBtn, false);
        this.setAtcLoading(mainBtn, false);
      });
  }

  setAtcLoading(btn, loading) {
    if (!btn) return;
    const textEl = btn.querySelector('.ph-atc-btn__text, .ph-sticky-bar__atc-text');
    if (!textEl) return;
    if (loading) {
      textEl.dataset.originalText = textEl.textContent;
      textEl.textContent = 'Adding...';
      btn.disabled = true;
      btn.classList.add('ph-atc-btn--loading');
    } else {
      textEl.textContent = textEl.dataset.originalText || 'Add to Cart';
      delete textEl.dataset.originalText;
      btn.disabled = false;
      btn.classList.remove('ph-atc-btn--loading');
    }
  }

  openNotifyModal() {
    const modal = this.querySelector('[data-ph-notify-modal]');
    if (!modal) return;
    modal.removeAttribute('hidden');
    const sid = this.dataset.section;
    const input = modal.querySelector(`#ph-notify-email-${sid}`);
    if (input) {
      input.value = '';
      input.focus();
    }
    modal.querySelector(`#ph-notify-form-${sid}`)?.removeAttribute('hidden');
    modal.querySelector(`#ph-notify-success-${sid}`)?.setAttribute('hidden', '');
    modal.querySelector(`#ph-notify-error-${sid}`)?.setAttribute('hidden', '');
  }

  initNotifyModal() {
    const sid = this.dataset.section;
    const modal = this.querySelector('[data-ph-notify-modal]');
    if (!modal) return;
    modal.querySelector('[data-ph-notify-close]')?.addEventListener('click', () => {
      modal.setAttribute('hidden', '');
    });
    modal.addEventListener('click', (e) => {
      if (e.target === modal) modal.setAttribute('hidden', '');
    });
    modal.querySelector('[data-ph-notify-submit]')?.addEventListener('click', () => {
      const emailInput = document.getElementById(`ph-notify-email-${sid}`);
      const submitBtn = document.getElementById(`ph-notify-submit-${sid}`);
      const errorEl = document.getElementById(`ph-notify-error-${sid}`);
      const formEl = document.getElementById(`ph-notify-form-${sid}`);
      const successEl = document.getElementById(`ph-notify-success-${sid}`);
      const email = emailInput?.value.trim() || '';
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        if (errorEl) {
          errorEl.textContent = 'Please enter a valid email address.';
          errorEl.removeAttribute('hidden');
        }
        return;
      }
      if (submitBtn) submitBtn.disabled = true;
      errorEl?.setAttribute('hidden', '');

      // Dev-gated: route to the custom BIS service instead of the Klaviyo list subscribe.
      const customBisEnabled = window.settings && window.settings.waitlist_custom_bis_enabled === true;
      const customBisEndpoint = (window.settings && window.settings.waitlist_custom_bis_endpoint) || '';
      if (customBisEnabled && customBisEndpoint) {
        const variantId = this.querySelector(`#product-form-${sid} input[name="id"]`)?.value || '';
        fetch(customBisEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            productId: window.__productIdFromTemplate || '',
            variantId,
            source: 'Jones Road PDP Waitlist',
          }),
        })
          .then((r) => r.json().then((d) => ({ d, r })))
          .then(({ d, r }) => {
            if (r.ok && d && d.ok) {
              formEl?.setAttribute('hidden', '');
              successEl?.removeAttribute('hidden');
              return;
            }
            if (errorEl) {
              errorEl.textContent = (d && d.error) || 'Something went wrong. Please try again.';
              errorEl.removeAttribute('hidden');
            }
            if (submitBtn) submitBtn.disabled = false;
          })
          .catch(() => {
            if (errorEl) {
              errorEl.textContent = 'Something went wrong. Please try again.';
              errorEl.removeAttribute('hidden');
            }
            if (submitBtn) submitBtn.disabled = false;
          });
        return;
      }

      const listId = getKlaviyoListId(sid);
      const body =
        `email=${encodeURIComponent(email)}` +
        `&g=${encodeURIComponent(listId)}` +
        '&%24consent=email' +
        '&%24fields=%24consent%2C%24source' +
        '&%24list_fields=%24consent' +
        '&%24source=Back+In+Stock' +
        `&%24timezone_offset=${encodeURIComponent(-(new Date().getTimezoneOffset() / 60))}`;
      fetch('https://manage.kmail-lists.com/ajax/subscriptions/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
        body,
      })
        .then((r) => r.json())
        .then((data) => {
          if (data && data.errors && data.errors.length) {
            if (errorEl) {
              errorEl.textContent = data.errors.join(', ');
              errorEl.removeAttribute('hidden');
            }
            if (submitBtn) submitBtn.disabled = false;
          } else {
            formEl?.setAttribute('hidden', '');
            successEl?.removeAttribute('hidden');
          }
        })
        .catch(() => {
          if (errorEl) {
            errorEl.textContent = 'Something went wrong. Please try again.';
            errorEl.removeAttribute('hidden');
          }
          if (submitBtn) submitBtn.disabled = false;
        });
    });
  }

  initFindationModal() {
    const modal = this.querySelector('[data-ph-findation]');
    if (!modal) return;
    this.addEventListener('click', (e) => {
      const openBtn = e.target.closest('[data-ph-findation-open]');
      if (openBtn && this.contains(openBtn)) {
        modal.removeAttribute('hidden');
      }
    });
    modal.querySelector('[data-ph-findation-close]')?.addEventListener('click', () => {
      modal.setAttribute('hidden', '');
    });
    modal.addEventListener('click', (e) => {
      if (e.target === modal) modal.setAttribute('hidden', '');
    });
  }

  initPairWithCards() {
    /* delegated in onDelegatedClick */
  }

  listenCartEvents() {
    document.addEventListener('liquid-ajax-cart:request-start', this._boundHandlers.onCartRequestStart);
    document.addEventListener('liquid-ajax-cart:request-end', this._boundHandlers.onCartRequestEnd);
  }

  onCartRequestStart(e) {
    const state = e.detail && e.detail.requestState;
    if (state && state.requestType === 'add') {
      const mainBtn = this.querySelector('[data-ph-main-atc]');
      const stickyBtn = this.querySelector('[data-ph-sticky-atc]');
      this.setAtcLoading(mainBtn, true);
      this.setAtcLoading(stickyBtn, true);
    }
  }

  onCartRequestEnd(e) {
    const state = e.detail && e.detail.requestState;
    if (state && state.requestType === 'add') {
      const mainBtn = this.querySelector('[data-ph-main-atc]');
      const stickyBtn = this.querySelector('[data-ph-sticky-atc]');
      this.setAtcLoading(mainBtn, false);
      this.setAtcLoading(stickyBtn, false);
      if (state.responseData && state.responseData.ok) {
        window.dispatchEvent(new CustomEvent('cart-open'));
      }
    }
  }

  playGalleryVideos() {
    this.querySelectorAll('.product-media__thumb-video, .product-media__hero-video').forEach((el) => {
      if (el.tagName !== 'VIDEO') return;
      el.muted = true;
      el.controls = false;
      const p = el.play?.();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    });
  }

  initSwiper() {
    const thumbnailGalleryEl = this.querySelector('.product-media-gallery__thumbnails');
    const thumbnailWrapperEl = this.querySelector('.product-media-gallery__thumbnails-wrapper');
    const mediaGalleryRoot = this.querySelector('media-gallery');
    const verticalThumbs =
      !!thumbnailGalleryEl &&
      !!mediaGalleryRoot &&
      mediaGalleryRoot.classList.contains('media-gallery--desktop-thumbnail') &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(min-width: 768px)').matches;

    if (thumbnailGalleryEl) {
      const mainGalleryEl = this.querySelector('.product-media-gallery__main');
      if (mainGalleryEl) {
        // The thumbnail strip's spacing is set by a CSS flex `gap` on the
        // `.swiper-wrapper` (~10px for the vertical strip). Swiper must use the
        // SAME value for `spaceBetween` or its content-length math is wrong:
        // with the previous hard-coded 4px, Swiper under-measured the strip by
        // ~30px, so `maxTranslate` stopped short and the down/next arrow hit
        // `isEnd` (disabled) before the last thumbnail was fully scrolled into
        // view. Read the real gap off the wrapper (pre-init) and match it.
        let thumbSpaceBetween = verticalThumbs ? 4 : 12;
        const thumbWrapperEl = thumbnailGalleryEl.querySelector('.swiper-wrapper');
        if (thumbWrapperEl) {
          const cssGap = parseFloat(getComputedStyle(thumbWrapperEl).rowGap);
          if (cssGap > 0) thumbSpaceBetween = Math.round(cssGap);
        }

        const thumbSwiperConfig = {
          direction: verticalThumbs ? 'vertical' : 'horizontal',
          spaceBetween: thumbSpaceBetween,
          slidesPerView: 'auto',
          freeMode: true,
          watchSlidesProgress: true,
          slideToClickedSlide: true,
          // `forceToAxis` makes the wheel only drive the thumbnail strip when
          // the user's primary wheel delta matches the strip's axis (vertical
          // wheel on vertical thumbs, horizontal/shift-wheel on horizontal).
          // Stops the thumbnail strip from hijacking normal page scrolling.
          mousewheel: { forceToAxis: true },
          // Re-measure on DOM/style mutations: the column is height-constrained
          // (to match the main image) after init, so without these Swiper can
          // keep a stale size and mis-compute the scrollable range.
          observer: true,
          observeParents: true,
          navigation: {
            prevEl: thumbnailWrapperEl?.querySelector('.swiper-button-prev') || thumbnailGalleryEl.querySelector('.swiper-button-prev'),
            nextEl: thumbnailWrapperEl?.querySelector('.swiper-button-next') || thumbnailGalleryEl.querySelector('.swiper-button-next'),
          },
        };
        if (!verticalThumbs) {
          thumbSwiperConfig.breakpoints = {
            768: { spaceBetween: 14 },
            1024: { spaceBetween: 16 },
          };
        }
        const thumbSwiper = new Swiper(thumbnailGalleryEl, thumbSwiperConfig);
        this.swiper = new Swiper(mainGalleryEl, {
          spaceBetween: 0,
          thumbs: { swiper: thumbSwiper },
          on: {
            slideChange: () => this.playGalleryVideos(),
          },
        });
        this.playGalleryVideos();
        thumbSwiper.on('slideChange', () => this.playGalleryVideos());

        // Belt-and-suspenders re-measure: the column height is set to match the
        // main image, which finishes sizing after the main image loads. Force a
        // thumb-swiper update once that settles (double rAF), on main-image load,
        // and whenever the column actually resizes — so `maxTranslate`/`isEnd`
        // (and the next-arrow enabled state) reflect the constrained height.
        const refreshThumbs = () => {
          if (thumbSwiper && !thumbSwiper.destroyed) thumbSwiper.update();
        };
        requestAnimationFrame(() => requestAnimationFrame(refreshThumbs));
        const mainImg = mainGalleryEl.querySelector('img');
        if (mainImg && !mainImg.complete) {
          mainImg.addEventListener('load', refreshThumbs, { once: true });
        }
        if ('ResizeObserver' in window) {
          let lastH = thumbnailGalleryEl.clientHeight;
          const ro = new ResizeObserver(() => {
            // Only update on a genuine height change to avoid feedback loops.
            const h = thumbnailGalleryEl.clientHeight;
            if (h !== lastH) {
              lastH = h;
              refreshThumbs();
            }
          });
          ro.observe(thumbnailGalleryEl);
        }
      }
    }

    const carouselGalleryEl = this.querySelector('.product-media-gallery__carousel');
    if (carouselGalleryEl) {
      const carouselSlides = carouselGalleryEl.querySelectorAll('.swiper-wrapper > .swiper-slide');
      const slideCount = carouselSlides.length;
      const isBundleNewMobileCarousel =
        this.classList.contains('bn-bundle-new') &&
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(max-width: 1024px)').matches;

      const carouselConfig = {
        autoHeight: true,
        direction: 'horizontal',
        pagination: {
          el: carouselGalleryEl.querySelector('.swiper-pagination'),
        },
        navigation: {
          prevEl: carouselGalleryEl.querySelector('.swiper-button-prev'),
          nextEl: carouselGalleryEl.querySelector('.swiper-button-next'),
        },
        on: {
          slideChange: () => this.playGalleryVideos(),
        },
      };

      if (isBundleNewMobileCarousel && slideCount > 1) {
        carouselConfig.loop = true;
        carouselConfig.loopedSlides = slideCount;
      }

      this.swiper = new Swiper(carouselGalleryEl, carouselConfig);
      this.playGalleryVideos();
    }
  }

  destroySwiper() {
    if (!this.swiper) return;
    try {
      if (this.swiper.thumbs && this.swiper.thumbs.swiper) {
        this.swiper.thumbs.swiper.destroy(true, true);
      }
      this.swiper.destroy(true, true);
    } catch {
      /* ignore */
    }
    this.swiper = undefined;
  }

  get variantSelector() {
    return this.querySelector('variant-selector');
  }

  get quantitySelector() {
    return this.querySelector('quantity-selector');
  }

  getProductJson() {
    if (this._productJsonCache !== undefined) {
      return this._productJsonCache || null;
    }
    const cacheKey = productJsonCacheKey(this.dataset.url);
    if (cacheKey && productJsonCacheByUrl.has(cacheKey)) {
      this._productJsonCache = productJsonCacheByUrl.get(cacheKey);
      return this._productJsonCache || null;
    }
    const el = this.querySelector('script[type="application/json"][data-product]');
    if (!el) {
      this._productJsonCache = false;
      return null;
    }
    try {
      this._productJsonCache = JSON.parse(el.textContent);
    } catch {
      this._productJsonCache = false;
      return null;
    }
    if (cacheKey && this._productJsonCache) {
      productJsonCacheByUrl.set(cacheKey, this._productJsonCache);
    }
    return this._productJsonCache || null;
  }

  /**
   * Match selected options to a variant using embedded product JSON (same source as the PDP).
   */
  findVariantForCurrentSelection() {
    const product = this.getProductJson();
    const variants = product?.variants;
    if (!Array.isArray(variants) || variants.length === 0) return null;
    const vs = this.variantSelector;
    if (!vs) return null;

    const byPos = {};
    if (vs.dataset.pickerType === 'dropdown') {
      vs.querySelectorAll('select').forEach((sel, i) => {
        const opt = sel.options[sel.selectedIndex];
        if (opt) byPos[i + 1] = opt.value;
      });
    } else {
      vs.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
        const m = input.name.match(/-(\d+)$/);
        if (m) byPos[parseInt(m[1], 10)] = input.value;
      });
    }
    const positions = Object.keys(byPos);
    if (positions.length === 0) return null;

    return (
      variants.find((v) => {
        for (const k of positions) {
          const pos = parseInt(k, 10);
          if (v[`option${pos}`] !== byPos[pos]) return false;
        }
        return true;
      }) || null
    );
  }

  applyOptimisticVariantUpdate() {
    const v = this.findVariantForCurrentSelection();
    if (!v) return;
    const mediaId = v.featured_media?.id ?? v.featured_image?.id;
    if (mediaId != null) this.updateMedia(mediaId);
    if (v.id != null) {
      this.updateVariantInputs(v.id);
      this.updateURL(v.id);
    }
    this.playGalleryVideos();
  }

  /**
   * Option value ids in option position order (1, 2, 3) for Section Rendering API.
   * @param {HTMLInputElement | null} [overrideRadio] If set, pretend this radio is selected for its option group (prefetch).
   */
  getOrderedOptionValueIds(overrideRadio = null) {
    const vs = this.variantSelector;
    if (!vs) return [];
    if (vs.dataset.pickerType === 'dropdown') {
      return Array.from(vs.querySelectorAll('select'))
        .map((select) => select.options[select.selectedIndex]?.dataset?.optionValueId)
        .filter((id) => id != null && id !== '');
    }
    const byPos = new Map();
    vs.querySelectorAll('input[type="radio"]:checked').forEach((r) => {
      const m = r.name.match(/-(\d+)$/);
      if (m) byPos.set(parseInt(m[1], 10), r.dataset.optionValueId);
    });
    if (overrideRadio && overrideRadio.name) {
      const m = overrideRadio.name.match(/-(\d+)$/);
      if (m) byPos.set(parseInt(m[1], 10), overrideRadio.dataset.optionValueId);
    }
    const out = [];
    for (let pos = 1; pos <= 3; pos++) {
      const id = byPos.get(pos);
      if (id != null && id !== '') out.push(id);
    }
    return out;
  }

  get selectedOptionValues() {
    return this.getOrderedOptionValueIds();
  }

  /** Warm section HTML for a hypothetical selection (hover) — dedupes with live `renderSection` fetches */
  prefetchSectionForInput(productUrl, input) {
    if (!productUrl || !input) return;
    if (input.dataset.productUrl && input.dataset.productUrl !== this.dataset.url) return;
    const sectionId =
      this.dataset.updateUrl === 'false' ? this.dataset.section.split('-modal')[0] : this.dataset.section;
    const optionValues = this.getOrderedOptionValueIds(input);
    if (optionValues.length === 0) return;
    loadSectionHtmlText(productUrl, sectionId, optionValues, null).catch(() => {});
  }

  getSelectedVariant(html) {
    const selectedVariant = html.querySelector('[data-selected-variant]')?.innerHTML;
    return !!selectedVariant ? JSON.parse(selectedVariant) : null;
  }

  onVariantChange(e) {
    const productUrlChanged = e.target?.dataset?.productUrl ? e.target?.dataset?.productUrl !== this.dataset.url : false;
    const productUrl = e.target?.dataset?.productUrl || this.dataset.url;
    this.renderSection(productUrlChanged, productUrl);
  }

  /**
   * Desktop hover: preview the same variant update path as click (section re-render + gallery).
   * Skipped on touch-first devices to avoid sticky hover states.
   */
  onVariantOptionHover(e) {
    if (typeof window.matchMedia === 'function' && window.matchMedia('(hover: none)').matches) {
      return;
    }
    const vs = this.variantSelector;
    if (!vs) return;

    const stickyItem = e.target.closest('.ph-sticky-bar__dropdown-item:not(.ph-sticky-bar__dropdown-item--disabled)');
    if (stickyItem && this.contains(stickyItem)) {
      const vid = stickyItem.dataset?.optionValueId;
      if (!vid) return;
      const input = vs.querySelector(`input[type="radio"][data-option-value-id="${CSS.escape(String(vid))}"]`);
      if (
        input &&
        !input.checked &&
        !input.disabled &&
        !input.classList.contains('disabled')
      ) {
        const pUrl = input.dataset.productUrl || this.dataset.url;
        this.prefetchSectionForInput(pUrl, input);
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    const label = e.target.closest('label[for]');
    if (!label || !vs.contains(label)) return;
    const forId = label.getAttribute('for');
    if (!forId) return;
    const input = document.getElementById(forId);
    if (!input || !vs.contains(input) || input.type !== 'radio') return;
    if (input.disabled || input.classList.contains('disabled')) return;
    if (input.checked) return;
    this.prefetchSectionForInput(input.dataset.productUrl || this.dataset.url, input);
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  onQuantitySelectorEvent(e) {
    const qs = this.quantitySelector;
    if (!qs) return;
    const quantityInput = qs.querySelector('input[type="number"]');
    let currentValue = parseInt(quantityInput.value, 10);
    const minValue = parseInt(quantityInput.getAttribute('min'), 10) || 0;
    const maxValue = parseInt(quantityInput.getAttribute('max'), 10) || Infinity;

    if (e.target.name === 'minus' && currentValue > minValue) {
      quantityInput.value = currentValue - 1;
    } else if (e.target.name === 'plus' && currentValue < maxValue) {
      quantityInput.value = currentValue + 1;
    } else if (e.type === 'change') {
      if (currentValue < minValue) {
        quantityInput.value = minValue;
      } else if (currentValue > maxValue) {
        quantityInput.value = maxValue;
      }
    }
  }

  updateMedia(variantFeaturedMediaId) {
    if (variantFeaturedMediaId == null || variantFeaturedMediaId === '') return;
    const id = String(variantFeaturedMediaId);
    const mediaSlide = this.querySelector(`.swiper-slide[data-media-id="${id}"]`);
    if (!mediaSlide) return;
    const slideIndex = parseInt(mediaSlide.dataset.mediaIndex, 10);
    if (!Number.isNaN(slideIndex)) this.swiper?.slideTo(slideIndex);
  }

  updateURL(variantId) {
    if (this.dataset.updateUrl === 'false') return;
    if (!window.location.pathname.includes('/products/')) return;
    window.history.replaceState({}, '', `${this.dataset.url}${variantId ? `?variant=${variantId}` : ''}`);
  }

  updateSourceFromDestination = (html, id) => {
    const source = html.getElementById(`${id}`);
    const destination = this.querySelector(`#${id}`);
    if (source && destination) {
      destination.innerHTML = source.innerHTML;
    }
  };

  updateVariantInputs(variantId) {
    this.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`).forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      if (input) input.value = variantId ?? '';
    });
  }

  applySectionHtmlResponse(responseText, productUrlChanged) {
    const html = new DOMParser().parseFromString(responseText, 'text/html');
    const variant = this.getSelectedVariant(html);
    if (productUrlChanged) {
      const productInfo = html.querySelector('product-info');
      this.replaceWith(productInfo);
      productInfo.updateURL(variant?.id);
    } else {
      const galleryId = `media-gallery-${this.dataset.section}`;
      const gallerySource = html.getElementById(galleryId);
      if (gallerySource) {
        this.destroySwiper();
        this.updateSourceFromDestination(html, galleryId);
      } else {
        this.updateMedia(variant?.featured_media?.id);
      }
      this.updateURL(variant?.id);
      this.updateVariantInputs(variant?.id);
      this.updateSourceFromDestination(html, `add-to-cart-container-${this.dataset.section}`);
      this.updateSourceFromDestination(html, `variant-selector-${this.dataset.section}`);
      this.updateSourceFromDestination(html, `price-${this.dataset.section}`);
      this.updateSourceFromDestination(html, `sku-${this.dataset.section}`);
      this.updateSourceFromDestination(html, `inventory-${this.dataset.section}`);
      this.updateSourceFromDestination(html, `shade-name-${this.dataset.section}`);
      this.updateSourceFromDestination(html, `sticky-bar-${this.dataset.section}`);
      this.updateSourceFromDestination(html, `shipping-msgs-${this.dataset.section}`);
      if (gallerySource) {
        this.initSwiper();
      }
    }
  }

  renderSection(productUrlChanged, productUrl) {
    this._sectionRenderGen += 1;
    const myGen = this._sectionRenderGen;

    if (!productUrlChanged) {
      this.applyOptimisticVariantUpdate();
    }

    const sectionId = this.dataset.updateUrl === 'false' ? this.dataset.section.split('-modal')[0] : this.dataset.section;
    const optionValues = this.selectedOptionValues;

    loadSectionHtmlText(productUrl, sectionId, optionValues, null)
      .then((responseText) => {
        if (myGen !== this._sectionRenderGen) return;

        if (this.dataset.updateUrl === 'false') {
          responseText = responseText.replaceAll(this.dataset.section.split('-modal')[0], this.dataset.section);
        }

        this.applySectionHtmlResponse(responseText, productUrlChanged);
      })
      .catch((error) => {
        if (myGen !== this._sectionRenderGen) return;
        console.error(error);
      });
  }
}

if (!customElements.get('product-info')) {
  customElements.define('product-info', ProductInfo);
}
